import React from 'react';

// One line-icon set for the admin panel.
//
// The pages had drifted to emoji (🏷️ 🚗 🔧 🗑️ …), which render as full-colour
// glyphs in whatever style the operating system ships — they never matched the
// monochrome stroke icons in the sidebar, and they look different on Windows,
// macOS and Android. These are the same family as Sidebar.js: 24×24 viewBox,
// no fill, currentColor stroke, round caps, 1.8 weight.
//
// Usage: <Icon name="trash" /> — inherits the parent's colour, so a button
// only has to set `color`. Pass `size` to override the default 16px.

const PATHS = {
  // Catalogue / navigation
  tag: 'M7 7h.01M3 6.5V10a2 2 0 00.586 1.414l7.5 7.5a2 2 0 002.828 0l4.5-4.5a2 2 0 000-2.828l-7.5-7.5A2 2 0 009.5 3.5H6a3 3 0 00-3 3z',
  car: 'M5 17a2 2 0 104 0m6 0a2 2 0 104 0M4 17h1m14 0h1M3 17v-4l2-5a2 2 0 011.9-1.4h10.2A2 2 0 0119 8l2 5v4M5 13h14',
  bike: 'M6 19a3 3 0 100-6 3 3 0 000 6zm12 0a3 3 0 100-6 3 3 0 000 6zM6 16l3.5-6H14m0 0l1.5 6M14 10l-1.5-3H10m8 3h-2',
  list: 'M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01',
  wrench: 'M14.7 6.3a4 4 0 105.03 5.03l-2.9-2.9m-2.13-2.13l2.13 2.13M14.7 6.3L17.6 3.4M9.9 11.1L4.2 16.8a2 2 0 102.8 2.8l5.7-5.7',
  store: 'M4 10v9a1 1 0 001 1h14a1 1 0 001-1v-9M3 7l1.5-3h15L21 7a3 3 0 01-6 0 3 3 0 01-6 0 3 3 0 01-6 0zM9 20v-5h6v5',

  // Actions
  edit: 'M11 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2v-5M17.5 3.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 8.5-8.5z',
  trash: 'M4 7h16M10 11v6M14 11v6M5 7l1 13a2 2 0 002 2h8a2 2 0 002-2l1-13M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3',
  eye: 'M2.5 12s3.5-6.5 9.5-6.5S21.5 12 21.5 12s-3.5 6.5-9.5 6.5S2.5 12 2.5 12zM12 14.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  check: 'M5 13l4 4L19 7',
  close: 'M6 6l12 12M18 6L6 18',
  copy: 'M9 9V5a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2h-4M5 9h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2v-8a2 2 0 012-2z',
  chevronDown: 'M6 9l6 6 6-6',
  search: 'M11 18a7 7 0 100-14 7 7 0 000 14zM20 20l-3.6-3.6',
  upload: 'M12 16V4m0 0L8 8m4-4l4 4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2',

  // Objects / state
  file: 'M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5zM14 3v5h5',
  folder: 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z',
  camera: 'M4 8h3l1.5-2.5h7L17 8h3a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1zM12 16.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z',
  video: 'M4 6h11a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1zM16 10l5-3v10l-5-3',
  mobile: 'M8 3h8a1 1 0 011 1v16a1 1 0 01-1 1H8a1 1 0 01-1-1V4a1 1 0 011-1zM11 18h2',
  broadcast: 'M12 13a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM8.5 15a5 5 0 010-7M15.5 8a5 5 0 010 7M5.6 18a9 9 0 010-12M18.4 6a9 9 0 010 12',
  pin: 'M12 21s7-6.3 7-11a7 7 0 10-14 0c0 4.7 7 11 7 11zM12 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  help: 'M12 21a9 9 0 100-18 9 9 0 000 18zM9.5 9.5a2.5 2.5 0 015 .3c0 1.7-2.5 2-2.5 3.7M12 17h.01',
  warning: 'M12 9v4m0 4h.01M10.3 3.9L2.4 17.5A2 2 0 004.1 20.5h15.8a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z',
  // ban/globe are inlined as CSS data-URIs by TrafficSignals; kept here so
  // every icon shape in the admin has one definition.
  ban: 'M12 21a9 9 0 100-18 9 9 0 000 18zM5.6 5.6l12.8 12.8',
  globe: 'M12 21a9 9 0 100-18 9 9 0 000 18zM3 12h18M12 3a14 14 0 000 18 14 14 0 000-18z',
  dot: 'M12 17a5 5 0 100-10 5 5 0 000 10z',
};

function Icon({ name, size = 16, strokeWidth = 1.8, style, title, ...rest }) {
  const d = PATHS[name];
  if (!d) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      // Decorative by default: the icons sit beside their own text label, so
      // announcing them again would just be noise.
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      style={{ flexShrink: 0, display: 'block', ...style }}
      {...rest}
    >
      {title && <title>{title}</title>}
      <path d={d} />
    </svg>
  );
}

export default Icon;
export { PATHS as ICON_NAMES };
